import Head from 'next/head';
import Router from 'next/router';
import { useEffect } from 'react';
import { trackHomePageNavigation, trackHomepageView } from 'lib/Tracking';
import Layout from 'components/Layout';
import Home from 'components/Home';
import Footer from 'components/Footer';
import StructuredDataComponent from 'components/StructuredData';
import StructuredData from 'lib/StructuredData/Organization';
import { HOMEPAGE_URL } from 'lib/ContentGroups';
import renderGeoIP from 'components/tags/GeoIP';
// assets
import openGraphHomepageImage from 'public/static/images/og_homepage_image.png';
import useAdSetup from 'lib/hooks/useAdSetup';
import { useContentGroups } from 'lib/hooks/useGoogleAnalytics';
import useGeoLocation from 'lib/hooks/useGeoLocation';
import { useTranslation } from 'react-i18next';
import CarGurusAdUnit from 'lib/CarGurusAds/AdUnit';
import { PageType, AgeType, Zone } from 'lib/CarGurusAds/TaxonomySchema';
import { GetServerSideProps } from 'next';
import isIndexable from 'lib/isIndexable';
import WebSiteStructuredData from 'components/Layout/WebSiteStructuredData';
import useEvents from 'lib/hooks/useEvents';
import PageViewEvent from 'lib/Tracking/HomePage/ViewEvent';

Router.events.on('routeChangeStart', (): void => {
  trackHomePageNavigation();
});

interface PageProps {
  indexable: boolean;
}

function IndexPage({ indexable }: PageProps): JSX.Element {
  const logger = useEvents();
  useEffect(() => {
    trackHomepageView();
    logger.log(PageViewEvent());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const carGurusAdUnit = new CarGurusAdUnit({ zone: Zone.Homepage });
  useContentGroups({ default: HOMEPAGE_URL });

  useAdSetup(carGurusAdUnit.toString(), {
    make: null,
    model: null,
    year: null,
    pageType: PageType.Homepage,
    ageType: AgeType.None,
    cpo: false,
    inLine: 1,
    body_style: null,
    category: null,
    app: 'web',
    sellerId: null,
  });

  const { t } = useTranslation();
  useGeoLocation(false);

  const structuredData = new StructuredData();

  return (
    <>
      <Layout
        title={t(`home.meta.pageTitle`)}
        description={t(`home.meta.metaDescription`)}
        alternateAndroidUrl="android-app://com.android.android/http/www.autolist.com/123"
        footer={<Footer />}
        includeAds={false}
        id="home"
        indexed={indexable}
      >
        <Head>
          {/* <link rel="preload" href={heroMobile} as="image" key="hero-img-mobile" />*/}
          <meta content={openGraphHomepageImage} property="og:image" key="og:image" />
          {renderGeoIP()}
        </Head>
        <Home />
      </Layout>
      <StructuredDataComponent data={structuredData} />
      <WebSiteStructuredData />
      <style jsx global>{`
        body {
          text-rendering: optimizeLegibility;
          text-shadow: 1px 1px 1px rgba(0, 0, 0, 0.004);
          font-weight: 400;
          font-smoothing: antialiased;
        }

        #home footer {
          text-rendering: auto;
          text-shadow: none;
          font-weight: 300;
          font-smoothing: antialiased;
        }

        #home .type,
        #home .location,
        #home .search .view-all,
        #home .search .view-all a,
        #home .full-row div,
        #home .subtext {
          font-weight: 300;
        }
      `}</style>
    </>
  );
}

export const getServerSideProps: GetServerSideProps<PageProps> = async ({ req, res }) => {
  res.setHeader('Cache-Control', 'public, max-age=900, stale-while-revalidate');
  const indexable = isIndexable(req);
  return { props: { indexable } };
};

export default IndexPage;
